import { useCallback, useState } from 'react';
import { debounce } from 'lodash';

/**
 * A custom hook that provides both immediate and debounced state values.
 * 
 * @param initial - The initial value for the state
 * @param delay - The debounce delay in milliseconds
 * @returns A tuple containing [immediateValue, debouncedValue, setValue]
 * 
 */
function useDebouncedState<T>(initial: T, delay: number): [T, T, (value: T) => void] {
  const [immediate, setImmediate] = useState<T>(initial);
  const [value, _setValue] = useState<T>(initial);

  const setDebounced = useCallback(debounce(_setValue, delay), [delay]);

  const setValue = useCallback((v: T) => {
    setImmediate(v);
    setDebounced(v);
  }, [setDebounced]);

  return [
    immediate,
    value,
    setValue
  ];
}

export default useDebouncedState;
